/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  PrinterBusinessService,
} from '@gh/builder-shared';
import { AelEvents, createLinkFromLetter } from '@gh/builder-shared-data';
import {
  ActionFlow,
  createFlow,
  eitherAction,
  exitFlow,
  getRouteInitData,
  getRouteParentTitle,
  goToStep,
  mapAction,
  streamOptionalValueAction,
} from '@gh/common-shared';
import { PRODUCE_STATUS_CHECK_INTERVAL } from '@gh/config';
import { isSelectionModelEmpty, RouteDataService, SelectionModel } from '@gh/core-data';
import { isMessageBasket } from '@gh/core-messages';
import { TRANSLATION_TUPLE_EMPTY, TranslationService, TranslationTuple } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { createLink, isNotNil, openInNewWindow, toString } from '@gh/core-util';
import { interval$, of$ } from '@gh/rx';
import { distinctUntilChanged$, filter$, map$, skip$, switchMap$ } from '@gh/rx/operators';

import { first, isEmpty, isEqual, noop, pick, tail } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import {
  getJobOrderListItemId,
  JobOrderDetailsAttachment,
  JobOrderLetter,
  JobOrderList,
  JobOrderListItem,
  jobOrderListProduce,
  JobOrderListProduceOptions,
  JobOrderListProduceStatus,
  JobOrderListSelectionFilter,
  JobOrderListSelectionType,
} from '../../resources/job-order-list.model';
import {
  JobOrderDetailsAttachmentDialogConfig,
  jobOrderDetailsAttachmentDialogRoute,
} from './job-order-details-attachment-dialog/job-order-details-attachment-dialog.component';
import { JobOrderDetailsFormState } from './job-order-details-form.service';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import { JobOrderDetailsListBusinessService } from '../../resources/job-order-details.service';

export type JobOrderPreviewFlowState = CommonFlowState<JobOrderListItem[]> & {
  currentLetter?: JobOrderLetter;
  currentOrder?: JobOrderListItem;
};

export type JobOrderProduceFlowState = {
  status: JobOrderListProduceStatus;
  orderList?: JobOrderList;
};

@Component({
  standalone: false,
             selector       : 'gh-job-order-details',
             templateUrl    : 'job-order-details.component.html',
             changeDetection: ChangeDetectionStrategy.OnPush,
             providers      : [JobOrderDetailsFormState],
           })
export class JobOrderDetailsComponent extends AbstractFormComponent<JobOrderList> implements OnInit, OnDestroy {
  parentTitle: Maybe<TranslationTuple>;
  hasParent: boolean;
  isCloud: boolean;

  private produceSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private routeData: RouteDataService,
    private dialogs: DialogService,
    private navigations: BuilderNavigationService,
    private notifications: NotificationService,
    private waitings: WaitingService,
    private translations: TranslationService,
    public form: JobOrderDetailsFormState,
    private business: JobOrderDetailsListBusinessService,
    private printers: PrinterBusinessService,
    private commonActions: CommonActions,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  get producingLabel(): string {
    const { progress } = this.form.produceStatus;
    return progress ? this.translations.get('lbl_job_orders_in_producing', {
      totalProduced: toString(progress.processed),
      totalAll     : toString(progress.total),
    }) : '';
  }

  ngOnInit(): void {
    const { orderList, printers } = getRouteInitData(this.route);

    const parentTitle = getRouteParentTitle(this.route);
    this.parentTitle = parentTitle || TRANSLATION_TUPLE_EMPTY;
    this.hasParent = !!parentTitle;
    this.isCloud = !!printers.isCloud;

    // listen for query params change in order to reload selected job
    this.route.params.pipe(
      skip$(1),
      switchMap$(({ jobId }: Params) => jobId
        ? this.business.findAllJobOrders(jobId).pipe(this.waitings.operator())
        : this.business.getEmptyJobOrders().pipe(this.waitings.operator())),
    )
        .subscribe(
          (list) => {
            this.form.build(list);
            this.validateAfterInit();
          },
          this.notifications.processAll,
        );

    this.form.build(orderList);
    this.validateAfterInit();

    // manual handling of change detection
    this.form.changes.subscribe(() => this.changeDetectorRef.markForCheck());

    // listen for change of produce status
    this.produceSubscription = this.form.produceStatus$.pipe(
      // changing of produce status affects tracking process ONLY IF job and/or producing flag has been changed
      map$((status) => pick(status, ['jobId', 'producing'])),
      distinctUntilChanged$(isEqual),
      switchMap$((status) => status.producing
        // if orders are producing now => check producing status each 10 seconds
        ? interval$(PRODUCE_STATUS_CHECK_INTERVAL).pipe(
          switchMap$(() => this.business.getProduceStatus(this.form.jobId)),
          // check each time if producing has been finished
          switchMap$((nextStatus) => nextStatus.producing
            ? [{ status: nextStatus, orderList: void 0 }]
            // if producing has been finished => get all orders again to get actual status of each order
            : this.business.findAllJobOrders(this.form.jobId).pipe(
              map$((nextOrderList) => ({ status: nextStatus, orderList: nextOrderList })))),
        )
        // if orders are not producing now => do nothing
        : [{ status, orderList: void 0 }]),
    )
                                   .subscribe(
                                     ({ status, orderList: nextOrderList }) => {
                                       // if we get full list of orders => reinit full form (with status), otherwise
                                       // update only status
                                       if (nextOrderList) {
                                         this.form.build(nextOrderList);
                                         if (!this.isCloud) {
                                           this.business.gets3Link(this.form.jobId).pipe(
                                             this.waitings.operator(),
                                             switchMap$((report) => this.showReportDialog(report)),
                                           )
                                               .subscribe({ next: noop, error: this.notifications.processAll });
                                         }
                                       } else {
                                         this.form.setTrackingStatus(status);
                                       }
                                     },
                                     this.notifications.processAll,
                                   );
  }

  ngOnDestroy(): void {
    this.produceSubscription.unsubscribe();
  }

  onOrderSelectionChange(selection: SelectionModel): void {
    this.form.setOrderSelection(selection);
  }

  on6aDocumentChange(has6ADocument: SelectionModel): void {
    this.form.set6aDocument(has6ADocument);
  }

  onSelectionTypeChange(type: JobOrderListSelectionType): void {
    this.form.setSelectionType(type);
  }

  onApplyFilter(filter: JobOrderListSelectionFilter): void {
    this.form.selectByFilter(filter);
  }

  onOptionsChange(options: JobOrderListProduceOptions): void {
    this.form.setOptions(options);
  }

  onOpenAttachments(): void {
    this.dialogs.openWithInput(jobOrderDetailsAttachmentDialogRoute, <JobOrderDetailsAttachmentDialogConfig>{
      folderId   : this.form.folderId,
      attachments: this.form.attachments,
    }).afterClosed().pipe(
      filter$(Boolean))
        .subscribe((attachments: JobOrderDetailsAttachment[]) => this.form.setAttachments(attachments));
  }

  onPreview(): void {
    this.previewFlow().run({
                             value: this.form.selectedOrders,
                           });
  }

  onReset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
        .subscribe(() => this.form.reset());
  }

  onProcess(): void {
    const { orderSelection } = this.form;

    if (isSelectionModelEmpty(orderSelection)) {
      return;
    }

    if (this.form.hasSelectedOrdersToPrint || this.form.forcePrint) {
      if (this.isCloud) {
        this.printers.choosePrinter({
                                      optional       : this.form.hasSelectedOrdersToEmail && !this.form.forcePrint,
                                      optionalMessage: 'msg_no_printer_email',
                                      finder         : {
                                        title: 'lbl_select_printer_for_producing',
                                      },
                                    }).pipe(switchMap$(({ authCode, printer }) =>
                                                         this.business.produce(
                                                           this.form.jobId,
                                                           jobOrderListProduce(
                                                             this.form.value, this.isCloud, printer, authCode),
                                                         ).pipe(
                                                           (this.waitings.operator()))))
        // start tracking producing process
            .subscribe(
              (status) => {
                this.notifications.success('msg_order_produce_tasks_added');
                this.form.switchToTrackingMode(status);
              },
              this.processErrors.bind(this),
            );
      } else {
        this.business.produce(this.form.jobId, jobOrderListProduce(this.form.value, this.isCloud)).pipe(
          (this.waitings.operator())).subscribe((status) => {
          this.notifications.success('msg_order_produce_tasks_added');
          this.form.switchToTrackingMode(status);
        },                                      this.processErrors.bind(this));
      }
    } else {
      this.business.produce(this.form.jobId, jobOrderListProduce(this.form.value, this.isCloud)).pipe(
        this.waitings.operator())
          .subscribe(
            (status) => {
              this.notifications.success('msg_order_produce_tasks_added');
              this.form.switchToTrackingMode(status);
            },
            this.processErrors.bind(this),
          );
    }
  }

  onPrintAllForEstimator(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_print_for_estimator')).pipe(
      filter$(yesAnswer),
    ).subscribe(
      (init) => {
        if (this.isCloud) {
          this.printers.choosePrinter({
                                        finder: {
                                          title: 'lbl_select_printer_for_producing',
                                        },
                                      }).pipe(switchMap$(({ printer, authCode }) =>
                                                           this.business.printAllForEstimator(
                                                             this.form.jobId, printer!, authCode!).pipe(
                                                             this.waitings.operator())))
          // start tracking producing process
              .subscribe(
                (status) => this.form.switchToTrackingMode(status),
                this.notifications.processAll,
              );
        } else {
          this.business.printAllForEstimator(this.form.jobId).pipe(
            this.waitings.operator())
          // start tracking producing process
              .subscribe(
                (status) => this.form.switchToTrackingMode(status),
                this.notifications.processAll,
              );
        }
      },
    );
  }

  onOpenReport(): void {
    openInNewWindow(this.form.value.reportUrl);
  }

  goToAel(): void {
    this.navigations.goToAel({
                               entityType  : 'JOB',
                               eventTypeIds: [AelEvents.Job.JobOrder.Produced],
                               label       : 'lbl_job_orders',
                             });
  }

  private validateAfterInit(): void {
    const { isJobCriteriaFailed, ordersPrintCostCenter } = this.form;
    if (isJobCriteriaFailed) {
      if (isNotNil(ordersPrintCostCenter)) {
        this.notifications.warning('msg_order_criteria', { code: toString(ordersPrintCostCenter) });
      } else {
        this.notifications.warning('msg_order_criteria_sys_vars');
      }
    }
  }

  private previewFlow(): ActionFlow<JobOrderPreviewFlowState> {
// @ts-expect-error Legacy typing suppression
    return createFlow({
                        'start': goToStep('nextOrder'),
                        // take next order
                        'nextOrder': mapAction((state: JobOrderPreviewFlowState) => ({
                          ...state,
                          value        : tail(state.value),
                          currentOrder : first(state.value),
                          currentLetter: void 0,
                        })).bindAs('hasNextOrder'),
                        // check if any order was taken
                        'hasNextOrder': eitherAction((state: JobOrderPreviewFlowState) => isNotNil(state.currentOrder))
                        .bindAs('processOrder', 'finish'),
                        // process current order
                        'processOrder': streamOptionalValueAction((state: JobOrderPreviewFlowState) =>
                                                                    of$(state.currentOrder).pipe(
                                                                      filter$(Boolean),
                                                                      switchMap$((order: any) => this.business.preview(
                                                                        this.form.jobId,
                                                                        getJobOrderListItemId(order),
                                                                      ).pipe((this.waitings.operator()))),
                                                                      map$(
                                                                        (currentLetter) => ({ ...state, currentLetter })),
                                                                    ))
                        .bindAs('showLink', 'error'),
                        // show dialog with link for current order
                        'showLink': this.commonActions.textMessage(
                          (state: JobOrderPreviewFlowState) => ({
                            title  : this.translations.get('lbl_preview_orders_for_producing'),
                            html   : [
                              this.translations.get('lbl_order_number'),
                              state.currentOrder!.orderNumber,
                              this.translations.get('lbl_for_cost_center'),
                              state.currentOrder!.costCenterCode,
                              this.translations.get('lbl_view_the_document_by_the_link'),
                              createLinkFromLetter(state.currentLetter!),
                            ].join(' '),
                            buttons: [
                              {
                                value   : 'next',
                                type    : 'primary',
                                title   : this.translations.get('lbl_generate_next'),
                                disabled: isEmpty(state.value),
                              },
                              { value: 'cancel', title: this.translations.get('lbl_cancel') },
                            ],
                          }),
                          { 'next': 'nextOrder', 'otherwise': 'finish' },
                        ),
                        // handle any errors
                        'error': this.commonActions.notifications().bindAs('finish'),
                        '*'    : exitFlow(),
                      });
  }

  private processErrors(errors: any): void {
    // if producing is in progress switch to tracking mode, otherwise run general error handling
    if (isMessageBasket(errors) && errors.has('msg_job_orders_produce_in_progress')) {
      this.business.getProduceStatus(this.form.jobId).pipe(
        this.waitings.operator())
          .subscribe((status) => this.form.setTrackingStatus(status));
    } else {
      this.notifications.processAll(errors);
    }
  }

  private showReportDialog(report: any): Observable<void> {
    if (report.s3Link) {
      return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title  : this.translations.get('Link to Print Job Orders'),
        html   : [
          this.translations.get('Please follow the link below to print Jobs Orders'),
          createLink(report.s3Link, 'Your file'),
        ].join(' '),
        buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
      }).afterClosed();
    } else {
      return of$();
    }
  }

}
